import { useEffect, useState } from "react";
import { api, unwrap } from "@/lib/api";
import { Truck, Plus, X, ChevronDown, ChevronUp, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { VENDORS } from "@/constants/testIds";

export function VendorsManager({ branches = [] }) {
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [poTarget, setPoTarget] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const refresh = () => {
    api.get("/vendors").then((r) => setItems(unwrap(r) || [])).catch(() => {});
  };

  useEffect(() => {
    if (open) {
      refresh();
      api.get("/inventory/alerts").then((r) => setAlerts(unwrap(r) || [])).catch(() => {});
    }
  }, [open]);

  const deactivate = async (id) => {
    try {
      await api.delete(`/vendors/${id}`);
      toast.success("Vendor deactivated");
      refresh();
    } catch {
      toast.error("Could not deactivate vendor");
    }
  };

  return (
    <div className="glass-primary rounded-[20px] overflow-hidden" data-testid={VENDORS.root}>
      <button
        onClick={() => setOpen((x) => !x)}
        data-testid={VENDORS.toggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/30 transition"
      >
        <div className="flex items-center gap-3">
          <Truck size={16} className="text-[var(--color-mauve-deep)]" />
          <div>
            <div className="font-sora font-semibold text-[15px] text-[var(--color-text-primary)]">Vendors & Purchase Orders</div>
            <div className="font-inter text-[12px] text-[var(--color-text-muted)]">
              Manage supplier contacts and send POs via WhatsApp
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {items.length > 0 && <span className="font-mono text-[12px] text-[var(--color-text-muted)]">{items.filter(v => v.is_active).length} active</span>}
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 anim-slide-up space-y-3">
          <div className="flex justify-end">
            <button data-testid={VENDORS.createBtn} onClick={() => setCreateOpen(true)} className="btn-3d btn-gold-3d btn-sm">
              <Plus size={14} /> Add vendor
            </button>
          </div>

          {items.length === 0 ? (
            <div className="font-inter text-[13px] text-[var(--color-text-muted)] text-center py-6">
              No vendors yet. Add one to start sending POs.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="font-inter text-[11px] uppercase tracking-[0.1em] text-[var(--color-text-muted)]">
                    <th className="py-2 pr-3">Vendor</th>
                    <th className="py-2 pr-3">Contact</th>
                    <th className="py-2 pr-3">Phone</th>
                    <th className="py-2 pr-3">Terms</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((v) => (
                    <tr key={v.id} data-testid={VENDORS.row(v.id)} className="border-t border-white/40">
                      <td className="py-2.5 pr-3 font-sora font-semibold text-[13px]">{v.name}</td>
                      <td className="py-2.5 pr-3 font-inter text-[12px] text-[var(--color-text-secondary)]">{v.contact_person || "—"}</td>
                      <td className="py-2.5 pr-3 font-mono text-[12px]">{v.phone}</td>
                      <td className="py-2.5 pr-3 font-inter text-[12px]">{v.payment_terms || "—"}</td>
                      <td className="py-2.5 pr-3">
                        <span className="status-pill" style={v.is_active ? { background: "rgba(22,163,74,0.10)", color: "#15803D" } : { background: "rgba(120,113,108,0.10)", color: "#78716C" }}>
                          {v.is_active ? "ACTIVE" : "OFF"}
                        </span>
                      </td>
                      <td className="py-2.5 flex items-center gap-1.5">
                        {v.is_active && (
                          <>
                            <button onClick={() => setPoTarget(v)} className="btn-3d btn-glass-3d btn-sm" data-testid={VENDORS.sendPoBtn(v.id)}>
                              <Send size={11} /> Send PO
                            </button>
                            <button onClick={() => deactivate(v.id)} className="w-7 h-7 rounded-full glass-subtle flex items-center justify-center hover:bg-white/80" aria-label="Deactivate">
                              <Trash2 size={12} className="text-[var(--color-danger-deep)]" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {createOpen && <CreateVendorModal branches={branches} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); refresh(); }} />}
      {poTarget && <SendPOModal vendor={poTarget} alerts={alerts} onClose={() => setPoTarget(null)} onSent={() => setPoTarget(null)} />}
    </div>
  );
}

function CreateVendorModal({ branches, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", contact_person: "", phone: "", email: "", address: "",
    products_supplied: "", payment_terms: "COD", gst_number: "",
    branch_id: "",
  });

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Name and phone are required"); return; }
    try {
      await api.post("/vendors", {
        name: form.name.trim(),
        contact_person: form.contact_person || null,
        phone: form.phone.trim(),
        email: form.email || null,
        address: form.address || null,
        products_supplied: form.products_supplied.split(",").map(s => s.trim()).filter(Boolean),
        payment_terms: form.payment_terms,
        gst_number: form.gst_number || null,
        is_active: true,
        branch_id: form.branch_id || null,
      });
      toast.success(`Vendor "${form.name}" added`);
      onCreated?.();
    } catch (e) {
      const data = e?.response?.data?.error;
      toast.error(data?.message || "Could not create vendor");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center anim-fade-in"
         style={{ background: "rgba(28,25,23,0.40)", backdropFilter: "blur(12px)" }}
         data-testid={VENDORS.createModal}>
      <div className="glass-elevated rounded-[24px] p-6 w-[min(520px,calc(100vw-32px))] max-h-[88vh] overflow-auto anim-slide-up-lg space-y-3">
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="overline mb-1">New vendor</div>
            <h2 className="font-display text-[22px] font-bold tracking-[-0.02em]">Add supplier</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Row label="Vendor name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid={VENDORS.nameInput} /></Row>
          <Row label="Contact person"><input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className={inputCls} data-testid={VENDORS.contactInput} /></Row>
        </div>
        <Row label="Phone *"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`${inputCls} font-mono`} data-testid={VENDORS.phoneInput} placeholder="+91 98765 43210" /></Row>
        <Row label="Email"><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></Row>
        <Row label="Address"><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></Row>
        <Row label="Products supplied (comma-separated)"><input value={form.products_supplied} onChange={(e) => setForm({ ...form, products_supplied: e.target.value })} className={inputCls} placeholder="Olaplex No.3, Majirel 5N" /></Row>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Payment terms">
            <select value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className={inputCls} data-testid={VENDORS.termsSelect}>
              <option>COD</option><option>Net 15</option><option>Net 30</option>
            </select>
          </Row>
          <Row label="GSTIN"><input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} className={`${inputCls} uppercase`} placeholder="27AABCV0000A1Z5" /></Row>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-3d btn-glass-3d btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-3d btn-gold-3d" onClick={submit} data-testid={VENDORS.submitBtn}><Plus size={14} /> Add vendor</button>
        </div>
      </div>
    </div>
  );
}

function SendPOModal({ vendor, alerts, onClose, onSent }) {
  // Prefill from low-stock items supplied by this vendor's products
  const matchingAlerts = (alerts || []).filter((it) =>
    (vendor.products_supplied || []).some((p) =>
      (it.name || "").toLowerCase().includes((p || "").toLowerCase())
    )
  );
  const [rows, setRows] = useState(() =>
    matchingAlerts.length > 0
      ? matchingAlerts.map((it) => ({
          item_id: it.id, item_name: it.name,
          qty: Math.max(1, (it.reorder_level || 0) - (it.stock_qty || 0)),
          unit: it.unit || "piece",
          price_per_unit: it.price_per_unit || 0,
        }))
      : [{ item_id: null, item_name: "", qty: 1, unit: "piece", price_per_unit: 0 }]
  );

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const total = rows.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.price_per_unit) || 0), 0);
  const setRow = (i, patch) => setRows(rows.map((r, j) => j === i ? { ...r, ...patch } : r));
  const addRow = () => setRows([...rows, { item_id: null, item_name: "", qty: 1, unit: "piece", price_per_unit: 0 }]);

  const submit = async () => {
    const items = rows.filter(r => r.item_name.trim());
    if (items.length === 0) { toast.error("Add at least one item"); return; }
    try {
      const r = await api.post(`/vendors/${vendor.id}/po`, { items });
      toast.success(`PO ${unwrap(r).po_ref} sent to ${vendor.name}`, {
        description: `Estimated ₹${unwrap(r).total_estimated_cost} · WhatsApp to ${vendor.phone}`,
      });
      onSent?.();
    } catch (e) {
      const data = e?.response?.data?.error;
      toast.error(data?.message || "Could not send PO");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center anim-fade-in"
         style={{ background: "rgba(28,25,23,0.40)", backdropFilter: "blur(12px)" }}
         data-testid={VENDORS.poModal}>
      <div className="glass-elevated rounded-[24px] p-6 w-[min(620px,calc(100vw-32px))] max-h-[88vh] overflow-auto anim-slide-up-lg space-y-3">
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="overline mb-1">Purchase order</div>
            <h2 className="font-display text-[22px] font-bold tracking-[-0.02em]">PO to {vendor.name}</h2>
            <p className="font-inter text-[12.5px] text-[var(--color-text-muted)] mt-1">{vendor.phone} · {vendor.payment_terms || "COD"}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>

        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_80px_120px_80px] gap-2">
              <input value={r.item_name} onChange={(e) => setRow(i, { item_name: e.target.value })}
                     className={inputCls} placeholder="Item name" data-testid={VENDORS.poItemInput(i)} />
              <input type="number" min="1" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })}
                     className={`${inputCls} font-mono`} data-testid={VENDORS.poQtyInput(i)} />
              <input type="number" min="0" step="0.5" value={r.price_per_unit} onChange={(e) => setRow(i, { price_per_unit: e.target.value })}
                     className={`${inputCls} font-mono`} placeholder="Price ₹" data-testid={VENDORS.poPriceInput(i)} />
              <div className="flex items-center justify-end font-mono text-[12.5px]">
                ₹{Math.round((Number(r.qty) || 0) * (Number(r.price_per_unit) || 0))}
              </div>
            </div>
          ))}
          <button onClick={addRow} data-testid={VENDORS.poAddItemBtn} className="btn-3d btn-glass-3d btn-sm">
            <Plus size={13} /> Add item
          </button>
        </div>

        <div className="rounded-2xl p-3 flex items-center justify-between" style={{ background: "rgba(212,168,83,0.08)", border: "1px solid rgba(212,168,83,0.28)" }}>
          <span className="overline">Estimated cost</span>
          <span className="font-mono text-[22px] text-[var(--color-gold-deeper)]">₹{Math.round(total)}</span>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-3d btn-glass-3d btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-3d btn-gold-3d" onClick={submit} data-testid={VENDORS.poSendBtn}>
            <Send size={14} /> Send purchase order
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[14px] text-[var(--color-text-primary)] outline-none focus:border-[var(--color-gold-mid)] focus:ring-2 focus:ring-[var(--color-gold-mid)]/20 transition";

function Row({ label, children }) {
  return (
    <label className="block">
      <div className="font-inter text-[12.5px] font-medium text-[var(--color-text-secondary)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
